import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  useMaterialReactTable,
} from 'material-react-table'
import {Box, darken} from '@mui/material'
import {useMemo, useState} from 'react'
import {AgreementChapterNew} from '../../app/pages/admin/trade-data-agreement-chapter-new/core/_models'
import {Col, Form, Pagination, Row} from 'react-bootstrap'

interface ReorderableDataTableProps {
  data: any[]
  columns: MRT_ColumnDef<any>[]
  onReorder: (draggingRow?: MRT_Row<any>, hoveredRow?: MRT_Row<any>) => void
  isLoading?: boolean
  isSaving?: boolean
  getRowId?: (originalRow: any, index: number, parentRow: MRT_Row<any>) => string
  getSubRows?: (originalRow: any, index: number) => any[] | undefined
  enableRowOrdering?: boolean
}

const ReorderableDataTable = ({
  data,
  columns,
  onReorder,
  isLoading = false,
  isSaving = false,
  getRowId,
  getSubRows,
  enableRowOrdering = true,
}: ReorderableDataTableProps) => {
  const [searchQuery, setSearchQuery] = useState('')

  const filterTree = (items: AgreementChapterNew[], query: string): AgreementChapterNew[] => {
    if (!query.trim()) return items

    const lowerQuery = query.toLowerCase()

    return items
      .map((item) => {
        // recursively filter children first
        const filteredChildren = item.children ? filterTree(item.children, query) : []

        // check if the current item itself matches
        const selfMatches =
          (item.title && item.title.toLowerCase().includes(lowerQuery)) ||
          (item.label && item.label.toLocaleLowerCase().includes(lowerQuery)) ||
          (item.type && item.type.toLocaleLowerCase().includes(lowerQuery))

        if (selfMatches || filteredChildren.length > 0) {
          return {...item, children: filteredChildren}
        }
        return null
      })
      .filter(Boolean) as AgreementChapterNew[]
  }

  const generatePages = (currentPage, totalPages) => {
    if (totalPages <= 7) {
      return Array.from({length: totalPages}, (_, i) => i + 1)
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5, '...', totalPages]
    }

    if (currentPage >= totalPages - 2) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages]
    }

    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages]
  }
  const pageSizes = [10, 20, 30, 40, 50, 100]

  const filteredDataTable = useMemo(() => filterTree(data, searchQuery), [data, searchQuery])

  const table = useMaterialReactTable({
    columns,
    data: filteredDataTable,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    enableColumnPinning: true,
    enableEditing: false,
    enableExpanding: true,
    enableSorting: false,
    enableColumnOrdering: false,
    enableRowOrdering: enableRowOrdering,
    enableExpandAll: false,
    enableColumnActions: false,
    displayColumnDefOptions: {
      'mrt-row-expand': {
        Header: () => null, // Just blank black header as requested
      },
      'mrt-row-drag': {
        Header: () => 'Reorder',
      },
    },
    getRowId,
    getSubRows,
    muiTableContainerProps: {
      sx: {maxHeight: 'calc(100vh - 200px)'},
    },
    icons: {
      ArrowDownwardIcon: (props: any) => {
        // Detect state explicitly from MUI classes to render exact fa-sort-down or fa-sort-up
        const isAsc =
          typeof props.className === 'string' && props.className.includes('DirectionAsc')
        return (
          <i
            {...props}
            className={`fa-solid text-white ${isAsc ? 'fa-sort-up' : 'fa-sort-down'} ${
              props.className || ''
            }`}
          />
        )
      },
      SyncAltIcon: ({style, direction, ...props}: any) => (
        <i {...props} className={`fa-solid text-white fa-sort ${props.className || ''}`} />
      ),
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: '#181C32',
        color: 'white',
        fontWeight: 'bold',
        fontSize: '1rem',
        paddingY: '7px',
        justifyContent: 'center',
        fontFamily: 'inherit',
        '& .Mui-TableHeadCell-Content': {
          justifyContent: 'center',
        },
        // Force opacity 1 so unsorted icons are white, not grayed out 0.3
        '& .MuiTableSortLabel-root': {
          opacity: '1 !important',
        },
        '& .MuiTableSortLabel-icon': {
          color: 'white !important',
          opacity: '1 !important',
          // Prevent MUI from spinning our custom FontAwesome icons upside down
          transform: 'none !important',
        },
      },
    },
    renderEmptyRowsFallback() {
      return (
        <div className='text-center text-muted py-4 w-100' style={{fontStyle: 'normal'}}>
          No data available
        </div>
      )
    },
    muiTableBodyCellProps: {
      sx: {
        fontSize: '1rem',
        paddingY: '10px',
        fontFamily: 'inherit',
      },
    },
    muiTableBodyRowProps: ({row}) => ({
      sx: {
        backgroundColor: row.depth === 0 ? '#f5f5f5' : 'inherit',
        '&:hover': {
          backgroundColor: darken('#f5f5f5', 0.1),
        },
      },
    }),
    muiTablePaperProps: {
      elevation: 0,
    },
    enableDensityToggle: false,
    enableColumnFilters: false,
    enableFullScreenToggle: false,
    renderTopToolbar: ({table}) => {
      const {pageIndex, pageSize} = table.getState().pagination
      const totalRows = table.getPrePaginationRowModel().rows.length
      const start = pageIndex * pageSize + 1
      const end = Math.min((pageIndex + 1) * pageSize, totalRows)

      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px',
          }}
        >
          <Box>
            <i className='fa-solid fa-circle text-success fs-9 me-2'></i>
            Showing {start} to {end} of {totalRows} results
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <Box sx={{display: 'flex', alignItems: 'center'}}>
              <i className='fa-solid fa-magnifying-glass fa-rotate-90 text-primary fs-9 me-2'></i>
              Search:
            </Box>
            <Form.Control
              type='text'
              placeholder='Search...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{maxWidth: '300px'}}
            />
          </Box>
        </Box>
      )
    },
    muiSearchTextFieldProps: {
      placeholder: 'Search...',
      sx: {minWidth: '300px'},
      variant: 'outlined',
      size: 'small',
    },
    renderBottomToolbar: ({table}) => {
      // 2. Extract current state to generate your pages array
      const pageIndex = table.getState().pagination.pageIndex
      const totalPages = table.getPageCount()
      const pages = generatePages(pageIndex + 1, totalPages)

      // 3. Return your custom Bootstrap row
      return (
        <Row className='my-3 align-items-center justify-content-between w-100 px-3'>
          <Col md={6} className='d-flex align-items-center'>
            Display
            <Form.Select
              size='sm'
              className='w-auto mx-2'
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value))
              }}
            >
              {pageSizes.map((pageSizeOption) => (
                <option key={pageSizeOption} value={pageSizeOption}>
                  {pageSizeOption}
                </option>
              ))}
            </Form.Select>{' '}
            rows per page
          </Col>

          <Col md={6} className='d-flex justify-content-end mt-2 mt-md-0'>
            <Pagination className='mb-0'>
              <Pagination.Prev
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              />

              {pages.map((page, index) =>
                page === '...' ? (
                  <Pagination.Ellipsis key={`ellipsis-${index}`} />
                ) : (
                  <Pagination.Item
                    key={index}
                    active={table.getState().pagination.pageIndex + 1 === page}
                    onClick={() => table.setPageIndex(Number(page) - 1)}
                  >
                    {page}
                  </Pagination.Item>
                )
              )}

              <Pagination.Next
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              />
            </Pagination>
          </Col>
        </Row>
      )
    },
    localization: {
      noRecordsToDisplay: 'No data available',
    },
    initialState: {
      columnVisibility: {'mrt-row-expand': false},
      columnPinning: {
        left: enableRowOrdering ? ['mrt-row-drag'] : [],
      },
      expanded: true,
      pagination: {pageSize: 10, pageIndex: 0},
      density: 'compact',
      showGlobalFilter: true,
    },
    state: {
      isLoading,
      isSaving,
    },
    muiRowDragHandleProps: ({table}) => ({
      onDragEnd: () => {
        const {draggingRow, hoveredRow} = table.getState()
        if (draggingRow && hoveredRow) {
          onReorder(draggingRow as MRT_Row<any>, hoveredRow as MRT_Row<any>)
        }
      },
    }),
  })

  return <MaterialReactTable table={table} />
}

export default ReorderableDataTable
